import { Button, Card, Table, Badge, Input, Select, Avatar, Progress, Space, Typography } from 'antd'
import {
  SearchOutlined,
  PlusOutlined,
  WarningOutlined,
  EyeOutlined,
  EditOutlined,
  BarChartOutlined,
} from '@ant-design/icons'
import type { TableColumnsType } from 'antd'

const { Title, Paragraph, Text } = Typography

interface AssessmentData {
  key: string
  name: string
  description: string
  workspace: string
  status: 'completed' | 'in-progress' | 'pending'
  responses: { current: number; total: number }
  completion: number
  lastUpdated: string
}

const assessmentData: AssessmentData[] = [
  {
    key: '1',
    name: 'Q4 Performance Review',
    description: '360-degree feedback form',
    workspace: 'Sales Team',
    status: 'completed',
    responses: { current: 47, total: 50 },
    completion: 94,
    lastUpdated: '2 hours ago',
  },
  {
    key: '2',
    name: 'Customer Satisfaction Survey',
    description: 'NPS and feedback collection',
    workspace: 'Support',
    status: 'in-progress',
    responses: { current: 23, total: 100 },
    completion: 23,
    lastUpdated: '5 hours ago',
  },
  {
    key: '3',
    name: 'Employee Onboarding Checklist',
    description: 'New hire orientation tasks',
    workspace: 'HR Department',
    status: 'completed',
    responses: { current: 15, total: 15 },
    completion: 100,
    lastUpdated: '1 day ago',
  },
  {
    key: '4',
    name: 'Product Feedback Collection',
    description: 'Feature requests and bugs',
    workspace: 'Product Team',
    status: 'pending',
    responses: { current: 0, total: 200 },
    completion: 0,
    lastUpdated: '2 days ago',
  },
  {
    key: '5',
    name: 'Quarterly Skills Assessment',
    description: 'Team competency evaluation',
    workspace: 'Engineering',
    status: 'in-progress',
    responses: { current: 67, total: 85 },
    completion: 79,
    lastUpdated: '3 hours ago',
  },
]

const getStatusBadge = (status: AssessmentData['status']) => {
  const config = {
    completed: { status: 'success' as const, text: 'Completed' },
    'in-progress': { status: 'processing' as const, text: 'In Progress' },
    pending: { status: 'default' as const, text: 'Pending' },
  }
  const { status: badgeStatus, text } = config[status]
  return <Badge status={badgeStatus} text={text} />
}

export function AssessmentsPage() {
  const columns: TableColumnsType<AssessmentData> = [
    {
      title: 'Assessment',
      dataIndex: 'name',
      key: 'name',
      width: 300,
      render: (name: string, record: AssessmentData) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.description}
          </Text>
        </div>
      ),
    },
    {
      title: 'Workspace',
      dataIndex: 'workspace',
      key: 'workspace',
      width: 150,
      render: (workspace: string) => (
        <Space>
          <Avatar size="small" style={{ backgroundColor: '#1890ff' }}>
            {workspace[0]}
          </Avatar>
          {workspace}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: AssessmentData['status']) => getStatusBadge(status),
    },
    {
      title: 'Responses',
      key: 'responses',
      width: 120,
      render: (_: unknown, record: AssessmentData) => (
        <span>
          <strong>{record.responses.current}</strong>
          <Text type="secondary"> / {record.responses.total}</Text>
        </span>
      ),
    },
    {
      title: 'Completion',
      dataIndex: 'completion',
      key: 'completion',
      width: 150,
      render: (completion: number) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Progress
            percent={completion}
            size="small"
            style={{ minWidth: 80, margin: 0 }}
            strokeColor={completion === 100 ? '#52c41a' : completion > 50 ? '#1890ff' : '#faad14'}
          />
          <Text strong style={{ fontSize: 13 }}>
            {completion}%
          </Text>
        </div>
      ),
    },
    {
      title: 'Last Updated',
      dataIndex: 'lastUpdated',
      key: 'lastUpdated',
      width: 130,
      render: (text: string) => <Text type="secondary">{text}</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: () => (
        <Space size="small">
          <Button type="text" size="small" icon={<BarChartOutlined />} />
          <Button type="text" size="small" icon={<EditOutlined />} />
          <Button type="text" size="small" icon={<EyeOutlined />} />
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 8 }}>
          Assessment Management
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Manage your assessment profiles and track completion rates across your organization
        </Paragraph>
      </div>

      {/* Action Bar */}
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: '16px 24px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            placeholder="Search assessments..."
            prefix={<SearchOutlined />}
            style={{ maxWidth: 300 }}
            size="large"
          />
          <Select
            defaultValue="all-status"
            style={{ width: 150 }}
            size="large"
            options={[
              { value: 'all-status', label: 'All Statuses' },
              { value: 'active', label: 'Active' },
              { value: 'completed', label: 'Completed' },
              { value: 'pending', label: 'Pending' },
            ]}
          />
          <Select
            defaultValue="all-workspace"
            style={{ width: 180 }}
            size="large"
            options={[
              { value: 'all-workspace', label: 'All Workspaces' },
              { value: 'sales', label: 'Sales Team' },
              { value: 'hr', label: 'HR Department' },
              { value: 'product', label: 'Product Team' },
            ]}
          />
          <div style={{ flex: 1 }} />
          <Button icon={<WarningOutlined />} size="large" danger>
            View Expiring (3)
          </Button>
          <Button type="primary" icon={<PlusOutlined />} size="large">
            New Assessment
          </Button>
        </div>
      </Card>

      {/* Data Table */}
      <Card
        title="Recent Assessments"
        extra={
          <Text type="secondary">
            Track and manage all assessment submissions across your organization
          </Text>
        }
      >
        <Table
          columns={columns}
          dataSource={assessmentData}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: total => `Total ${total} assessments`,
          }}
          size="middle"
        />
      </Card>
    </div>
  )
}

